// Decides what a completed drag-to-create gesture on a Gantt asset row means.
//
// Split out of schedule.tsx so the mouse-vs-touch rules are testable without a
// DOM. The row gesture has to serve two input models that want opposite things
// from the same pixels:
//
//   Mouse — press, sweep, release authors an explicit time RANGE. Nothing else
//           competes for a horizontal sweep, so any travel is intentional.
//   Touch — a horizontal sweep on empty row space is how the user PANS the
//           grid; it is the only scroll affordance there. Treating that as a
//           range would make the board unscrollable, so touch creates only on
//           a short, near-stationary tap and lets everything else fall through
//           to native scrolling.

/** Travel (px) a touch may drift and still count as a tap rather than a pan. */
export const TAP_SLOP_PX = 10;
/** Longest (ms) a touch may rest and still count as a tap. Past this the user
 *  is holding rather than tapping, which we treat as a pan and ignore. */
export const TAP_MAX_MS = 700;

export interface RowGesture {
  /** PointerEvent.pointerType — 'mouse' | 'touch' | 'pen'. */
  pointerType: string;
  /** Row-relative x at pointerdown. */
  startX: number;
  /** Row-relative x at pointerup. */
  currentX: number;
  /** Event timeStamp (DOMHighResTimeStamp) at pointerdown / pointerup. */
  startedAt: number;
  endedAt: number;
}

/**
 * Row-relative x span the new job should cover, or null when the gesture was a
 * pan (or otherwise not a create). A tap returns a zero-width span; the caller
 * widens that to one hour, matching the existing click-to-create behaviour.
 */
export function rowGestureRange(g: RowGesture): { left: number; right: number } | null {
  if (g.pointerType === 'mouse') {
    return {
      left: Math.min(g.startX, g.currentX),
      right: Math.max(g.startX, g.currentX),
    };
  }
  if (Math.abs(g.currentX - g.startX) > TAP_SLOP_PX) return null;
  if (g.endedAt - g.startedAt > TAP_MAX_MS) return null;
  return { left: g.startX, right: g.startX };
}

/**
 * Whether an in-flight gesture should paint the drag-to-create ghost bar.
 *
 * Only mouse does. A touch that has not ended yet is indistinguishable from the
 * start of a pan, so painting a ghost under the finger would flash a phantom
 * job every time the user scrolls the board.
 */
export function showsGhost(pointerType: string): boolean {
  return pointerType === 'mouse';
}
